/*
 * cv-v3-modern.js — Data-led interactions for Fiabilité v3
 *
 * Vanilla IIFE, zero dependencies, SSR-safe. Loads with `defer`.
 * Progressive enhancement: all widgets have functional baselines without this file.
 *
 * Modules:
 * - readingProgress: 2px scroll bar
 * - verdictPill: View Transitions slide-in on scroll past hero
 * - radar: eased morph 0→1 via CSS --cv-v3-radar-t on scroll-into-view
 * - reliability: Canvas 2D stacked bars or SVG bar fallback with staggered fills
 * - quiz: Web Animations API slide between steps, scoring against motor rules
 * - tco: realtime calc with requestAnimationFrame throttle
 * - obdTooltips: delegated hover/focus on [data-cv-obd] chips
 */
(function () {
  'use strict';

  var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function raf(fn) { return (window.requestAnimationFrame || function (cb) { return setTimeout(cb, 16); })(fn); }
  function onReady(fn) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn, { once: true });
    else fn();
  }
  function throttleRAF(fn) {
    var ticking = false;
    return function () {
      if (ticking) return;
      ticking = true;
      var ctx = this, args = arguments;
      raf(function () { fn.apply(ctx, args); ticking = false; });
    };
  }
  function debounce(fn, ms) {
    var t;
    return function () {
      var ctx = this, args = arguments;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, ms);
    };
  }
  function fmtFR(n) {
    return (Math.round(n * 100) / 100).toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, '\u202f');
  }
  function setChildText(parent, selector, text) {
    var el = parent.querySelector(selector);
    if (el) el.textContent = text;
  }
  function clearChildren(node) { while (node.firstChild) node.removeChild(node.firstChild); }
  function makeDiv(className, text) {
    var d = document.createElement('div');
    if (className) d.className = className;
    if (text !== undefined) d.textContent = text;
    return d;
  }

  // ---------- reading progress ----------------------------------------------
  function initProgress() {
    var el = document.querySelector('.cv-v3-progress__bar');
    if (!el) return;
    var docEl = document.documentElement;
    var update = throttleRAF(function () {
      var scrolled = window.scrollY || window.pageYOffset;
      var height = Math.max(1, (docEl.scrollHeight - window.innerHeight));
      var progress = Math.min(1, Math.max(0, scrolled / height));
      el.style.setProperty('--cv-v3-progress', progress.toFixed(4));
    });
    window.addEventListener('scroll', update, { passive: true });
    update();
  }

  // ---------- verdict pill ---------------------------------------------------
  function initVerdictPill() {
    var pill = document.querySelector('.cv-v3-verdict-pill');
    if (!pill) return;
    var sentinel = document.querySelector('.cv-v3-hero-sentinel') ||
                   document.querySelector('.cv-article-v2__featured') ||
                   document.querySelector('.cv-article-v2__editorial-lead');
    if (!sentinel) return;

    var show = function () {
      if (document.startViewTransition && !prefersReducedMotion) {
        document.startViewTransition(function () {
          pill.classList.add('cv-v3-verdict-pill--visible');
        });
      } else {
        pill.classList.add('cv-v3-verdict-pill--visible');
      }
    };
    var hide = function () {
      if (document.startViewTransition && !prefersReducedMotion) {
        document.startViewTransition(function () {
          pill.classList.remove('cv-v3-verdict-pill--visible');
        });
      } else {
        pill.classList.remove('cv-v3-verdict-pill--visible');
      }
    };

    // Inject a mobile close button if not present
    if (!pill.querySelector('.cv-v3-verdict-pill__close')) {
      var closeBtn = document.createElement('button');
      closeBtn.type = 'button';
      closeBtn.className = 'cv-v3-verdict-pill__close';
      closeBtn.setAttribute('aria-label', 'Fermer');
      closeBtn.textContent = '×';
      pill.appendChild(closeBtn);
      closeBtn.addEventListener('click', function () {
        pill.classList.remove('cv-v3-verdict-pill--visible');
        pill.dataset.userDismissed = 'true';
      });
    }

    var realShow = show;
    show = function () {
      if (pill.dataset.userDismissed === 'true') return;
      realShow();
    };

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) hide();
        else show();
      });
    }, { threshold: 0, rootMargin: '-40% 0px 0px 0px' });
    io.observe(sentinel);

    // Auto-dismiss when an input/select/textarea is focused (mobile: pill covers quiz/TCO)
    var focusable = 'input, select, textarea';
    document.addEventListener('focusin', function (e) {
      if (e.target && e.target.matches && e.target.matches(focusable)) {
        document.documentElement.classList.add('cv-v3-input-focused');
      }
    });
    document.addEventListener('focusout', function (e) {
      if (e.target && e.target.matches && e.target.matches(focusable)) {
        document.documentElement.classList.remove('cv-v3-input-focused');
      }
    });
  }

  // ---------- problem-grid severity detection --------------------------------
  function initProblemSeverity() {
    var cards = document.querySelectorAll('.cv-v2-problem-card');
    if (!cards.length) return;
    var costRe = /(\d{1,3}(?:[\s\u202f]?\d{3})*)(?:\s*(?:à|[-–])\s*(\d{1,3}(?:[\s\u202f]?\d{3})*))?\s*€/g;
    var criticalKeywords = /(casse moteur|moteur hs|remplacement moteur|sortie siv|vol|sinistre grave)/i;

    cards.forEach(function (card) {
      if (card.dataset.severity) return;
      var text = card.textContent || '';
      var maxCost = 0;
      var m;
      costRe.lastIndex = 0;
      while ((m = costRe.exec(text)) !== null) {
        var hi = (m[2] || m[1]).replace(/[\s\u202f]/g, '');
        var n = parseInt(hi, 10);
        if (!isNaN(n) && n > maxCost) maxCost = n;
      }
      var sev = 'low';
      if (criticalKeywords.test(text) || maxCost >= 2000) sev = 'critical';
      else if (maxCost >= 800) sev = 'high';
      else if (maxCost >= 200) sev = 'medium';
      else if (maxCost > 0) sev = 'low';
      else sev = 'medium';
      card.setAttribute('data-severity', sev);
    });
  }

  // ---------- shared vertex tooltip ------------------------------------------
  var vertexTooltip = null;
  function ensureVertexTooltip() {
    if (vertexTooltip) return vertexTooltip;
    vertexTooltip = document.createElement('div');
    vertexTooltip.className = 'cv-v3-tooltip cv-v3-tooltip--small';
    vertexTooltip.setAttribute('role', 'tooltip');
    document.body.appendChild(vertexTooltip);
    return vertexTooltip;
  }
  function showVertexTooltip(vertex) {
    var tip = ensureVertexTooltip();
    clearChildren(tip);
    var label = vertex.dataset.cvAxis || 'Axe';
    var score = vertex.dataset.cvScore || '—';
    tip.appendChild(makeDiv('cv-v3-tooltip__code', label));
    tip.appendChild(makeDiv('', score + '/10'));
    tip.appendChild(makeDiv('cv-v3-tooltip__meta', 'Repère éditorial Car Verif · non statistique'));
    var rect = vertex.getBoundingClientRect();
    var tw = tip.offsetWidth || 180, th = tip.offsetHeight || 60;
    var left = rect.left + rect.width / 2 - tw / 2;
    var top = rect.top - th - 8;
    if (top < 8) top = rect.bottom + 8;
    left = Math.max(8, Math.min(window.innerWidth - tw - 8, left));
    tip.style.left = Math.round(left) + 'px';
    tip.style.top = Math.round(top) + 'px';
    tip.classList.add('is-visible');
  }
  function hideVertexTooltip() {
    if (vertexTooltip) vertexTooltip.classList.remove('is-visible');
  }

  // ---------- radar chart ----------------------------------------------------
  function initRadars() {
    var radars = document.querySelectorAll('.cv-v3-radar[data-cv-radar]');
    if (!radars.length) return;
    var svgNS = 'http://www.w3.org/2000/svg';
    var labels = ['Fiabilité', 'Entretien', 'Consommation', 'Résiduel', 'Confort', 'Sécurité'];

    radars.forEach(function (root) {
      var poly = root.querySelector('.cv-v3-radar__vehicle-polygon');
      var ptsFinal = poly && poly.dataset.pointsFinal;
      if (!poly || !ptsFinal) return;
      var pairs = ptsFinal.trim().split(/\s+/).map(function (p) {
        var xy = p.split(',').map(Number);
        return { x: xy[0], y: xy[1] };
      });
      // Empty state: all at center = data missing
      var allAtCenter = pairs.every(function (p) { return Math.abs(p.x - 200) < 1 && Math.abs(p.y - 200) < 1; });
      if (allAtCenter) {
        root.classList.add('cv-v3-radar--empty');
        var empty = document.createElement('p');
        empty.className = 'cv-v3-radar__empty';
        empty.textContent = 'Données radar en cours d’analyse par notre équipe.';
        clearChildren(root);
        root.appendChild(empty);
        return;
      }
      // Compute average score for severity fill
      var avg = 0;
      pairs.forEach(function (p) {
        var dx = p.x - 200, dy = p.y - 200;
        avg += Math.sqrt(dx * dx + dy * dy) / 16;
      });
      avg /= pairs.length;
      root.setAttribute('data-avg', avg >= 7.2 ? 'high' : avg >= 5.5 ? 'medium' : 'low');

      var svg = root.querySelector('.cv-v3-radar__svg');
      if (svg) {
        pairs.forEach(function (p, i) {
          var dot = document.createElementNS(svgNS, 'circle');
          dot.setAttribute('class', 'cv-v3-radar__vertex-dot');
          dot.setAttribute('cx', p.x);
          dot.setAttribute('cy', p.y);
          dot.setAttribute('r', '3.5');
          svg.appendChild(dot);
          var hit = document.createElementNS(svgNS, 'circle');
          hit.setAttribute('class', 'cv-v3-radar__vertex');
          hit.setAttribute('cx', p.x);
          hit.setAttribute('cy', p.y);
          hit.setAttribute('r', '14');
          hit.setAttribute('tabindex', '0');
          hit.setAttribute('role', 'img');
          var dx = p.x - 200, dy = p.y - 200;
          var sc = Math.round(Math.sqrt(dx * dx + dy * dy) / 16 * 10) / 10;
          var scLabel = sc.toFixed(1).replace('.', ',');
          hit.setAttribute('aria-label', labels[i] + ' ' + scLabel + ' sur 10');
          hit.dataset.cvAxis = labels[i];
          hit.dataset.cvScore = scLabel;
          svg.appendChild(hit);
        });
      }
      root.addEventListener('mouseover', function (e) {
        var t = e.target;
        if (t && t.classList && t.classList.contains('cv-v3-radar__vertex')) showVertexTooltip(t);
      });
      root.addEventListener('focusin', function (e) {
        var t = e.target;
        if (t && t.classList && t.classList.contains('cv-v3-radar__vertex')) showVertexTooltip(t);
      });
      root.addEventListener('mouseout', hideVertexTooltip);
      root.addEventListener('focusout', hideVertexTooltip);
    });

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var root = entry.target;
        if (root.classList.contains('is-animated') || root.classList.contains('cv-v3-radar--empty')) { io.unobserve(root); return; }
        var poly = root.querySelector('.cv-v3-radar__vehicle-polygon');
        if (poly && poly.dataset.pointsFinal) {
          poly.setAttribute('points', poly.dataset.pointsFinal);
        }
        root.classList.add('is-animated');
        io.unobserve(root);
      });
    }, { threshold: 0.3 });
    radars.forEach(function (r) { io.observe(r); });
  }

  // ---------- reliability chart (SVG curve — Wave 6) ----------------------
  function initReliability() {
    var nodes = document.querySelectorAll('.cv-v3-reliability[data-cv-reliability]');
    if (!nodes.length) return;

    nodes.forEach(function (root) {
      var data = [];
      try { data = JSON.parse(root.dataset.cvReliability || '[]'); }
      catch (e) { data = []; }
      // Empty state: no data or all scores ≤ 0 → prose fallback
      var allEmpty = !data.length || data.every(function (d) { return !d || !d.score || d.score <= 0; });
      if (allEmpty) {
        if (!root.querySelector('.cv-v3-reliability__empty')) {
          var emptyP = document.createElement('p');
          emptyP.className = 'cv-v3-reliability__empty';
          emptyP.textContent = 'Fiabilité par tranche kilométrique en cours d’analyse — revenez d’ici 15 jours.';
          root.appendChild(emptyP);
        }
        return;
      }

      // --- Curve mode (Wave 6 default) — SVG line chart with hover tooltips
      if (root.dataset.cvReliabilityMode === 'curve' || root.classList.contains('cv-v3-reliability--curve')) {
        renderReliabilityCurve(root, data);
        return;
      }

      // --- Legacy Canvas mode fallback (unchanged)
      var supportsCanvas = (function () {
        try { return !!document.createElement('canvas').getContext('2d'); }
        catch (e) { return false; }
      })();
      if (!supportsCanvas || prefersReducedMotion) {
        root.dataset.fallback = 'true';
        var io2 = new IntersectionObserver(function (ee) {
          ee.forEach(function (e) {
            if (e.isIntersecting) {
              root.classList.add('is-animated');
              io2.unobserve(root);
            }
          });
        }, { threshold: 0.3 });
        io2.observe(root);
        return;
      }
      var canvas = root.querySelector('.cv-v3-reliability__canvas');
      if (!canvas) return;

      var render = function (progress) {
        var rect = canvas.getBoundingClientRect();
        var dpr = window.devicePixelRatio || 1;
        var heightPx = rect.width < 420 ? 240 : 280;
        canvas.style.height = heightPx + 'px';
        canvas.width = Math.max(320, rect.width * dpr);
        canvas.height = heightPx * dpr;
        var ctx = canvas.getContext('2d');
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.clearRect(0, 0, rect.width, heightPx);
        var padding = rect.width < 420 ? 32 : 48;
        var barCount = data.length || 4;
        var availW = rect.width - padding * 2;
        var barW = Math.min(88, Math.max(28, availW / barCount - 14));
        var gap = barCount > 1 ? (availW - barW * barCount) / (barCount - 1) : 0;
        var maxH = heightPx - 80;
        var baseY = heightPx - 40;

        ctx.save();
        ctx.strokeStyle = 'oklch(0.9 0.014 145)';
        ctx.lineWidth = 1;
        for (var t = 0; t <= 10; t += 2) {
          var y = baseY - (t / 10) * maxH;
          ctx.beginPath();
          ctx.moveTo(padding, y);
          ctx.lineTo(rect.width - padding, y);
          ctx.stroke();
          ctx.fillStyle = 'oklch(0.48 0.022 155)';
          ctx.font = '500 11px "Spline Sans Mono", monospace';
          ctx.textAlign = 'right';
          ctx.textBaseline = 'middle';
          ctx.fillText(String(t), padding - 8, y);
        }
        ctx.restore();

        data.forEach(function (d, i) {
          var x = padding + i * (barW + gap);
          var tProg = Math.max(0, Math.min(1, (progress - i * 0.15) * 1.3));
          var eased = tProg < 1 ? 1 - Math.pow(1 - tProg, 3) : 1;
          var val = (d.score || 0) * eased;
          var h = (val / 10) * maxH;
          var color = val >= 7.5
            ? 'oklch(0.67 0.16 152)'
            : val >= 5.5
              ? 'oklch(0.8 0.12 90)'
              : 'oklch(0.6 0.17 25)';
          ctx.fillStyle = color;
          ctx.fillRect(x, baseY - h, barW, h);
          if (eased > 0.5) {
            ctx.fillStyle = 'oklch(0.22 0.02 155)';
            ctx.font = '700 13px "Spline Sans Mono", monospace';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'bottom';
            ctx.fillText(val.toFixed(1).replace('.', ','), x + barW / 2, baseY - h - 4);
          }
          ctx.fillStyle = 'oklch(0.48 0.022 155)';
          ctx.font = '500 11px "Spline Sans Mono", monospace';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'top';
          ctx.fillText(d.bracket || ('' + i), x + barW / 2, baseY + 8);
        });
      };

      var progress = 0;
      var start = null;
      var animate = function (t) {
        if (start === null) start = t;
        var elapsed = t - start;
        progress = Math.min(1, elapsed / 1000);
        render(progress);
        if (progress < 1) raf(animate);
      };

      var io2 = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting && !root.classList.contains('is-animated')) {
            root.classList.add('is-animated');
            raf(animate);
            io2.unobserve(root);
          }
        });
      }, { threshold: 0.3 });
      io2.observe(root);

      var onResize = throttleRAF(function () {
        if (root.classList.contains('is-animated')) render(1);
      });
      window.addEventListener('resize', onResize, { passive: true });
    });
  }

  // Render SVG curve (viewBox 0 0 640 280). Grid: 0=y232, 10=y40. X: 60→600.
  function renderReliabilityCurve(root, data) {
    var n = data.length;
    if (n < 2) return;
    var svg = root.querySelector('.cv-v3-reliability__svg');
    var pathEl = root.querySelector('[data-cv-reliability-path]');
    var areaEl = root.querySelector('[data-cv-reliability-area]');
    var pointsG = root.querySelector('[data-cv-reliability-points]');
    var xaxisG = root.querySelector('[data-cv-reliability-xaxis]');
    var tooltip = root.querySelector('[data-cv-reliability-tooltip]');
    if (!svg || !pathEl || !pointsG) return;

    var xMin = 60, xMax = 600, yTop = 40, yBottom = 232;
    var span = xMax - xMin;
    var pts = data.map(function (d, i) {
      var x = xMin + (span * i) / (n - 1);
      var score = Math.max(0, Math.min(10, Number(d.score) || 0));
      var y = yBottom - (score / 10) * (yBottom - yTop);
      return { x: x, y: y, bracket: String(d.bracket || ''), score: score, notes: String(d.notes || '') };
    });

    // Smooth curve via Catmull-Rom → Bezier
    function pathSmooth(points) {
      if (points.length < 2) return '';
      var d = 'M ' + points[0].x.toFixed(1) + ',' + points[0].y.toFixed(1);
      for (var i = 0; i < points.length - 1; i++) {
        var p0 = points[i - 1] || points[i];
        var p1 = points[i];
        var p2 = points[i + 1];
        var p3 = points[i + 2] || p2;
        var c1x = p1.x + (p2.x - p0.x) / 6;
        var c1y = p1.y + (p2.y - p0.y) / 6;
        var c2x = p2.x - (p3.x - p1.x) / 6;
        var c2y = p2.y - (p3.y - p1.y) / 6;
        d += ' C ' + c1x.toFixed(1) + ',' + c1y.toFixed(1)
           + ' ' + c2x.toFixed(1) + ',' + c2y.toFixed(1)
           + ' ' + p2.x.toFixed(1) + ',' + p2.y.toFixed(1);
      }
      return d;
    }

    var dStr = pathSmooth(pts);
    pathEl.setAttribute('d', dStr);

    // Area under the curve (for fill)
    var areaD = dStr + ' L ' + pts[n - 1].x.toFixed(1) + ',' + yBottom
               + ' L ' + pts[0].x.toFixed(1) + ',' + yBottom + ' Z';
    if (areaEl) areaEl.setAttribute('d', areaD);

    // Data points — 2 circles per point (hit area + visible)
    while (pointsG.firstChild) pointsG.removeChild(pointsG.firstChild);
    pts.forEach(function (pt, i) {
      var sev = pt.score >= 7.5 ? 'low' : (pt.score >= 5.5 ? 'medium' : 'high');

      var hit = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
      hit.setAttribute('cx', pt.x);
      hit.setAttribute('cy', pt.y);
      hit.setAttribute('r', 14);
      hit.setAttribute('class', 'cv-v3-reliability__point-hit');
      hit.setAttribute('data-idx', String(i));
      hit.setAttribute('tabindex', '0');
      hit.setAttribute('role', 'img');
      hit.setAttribute('aria-label', pt.bracket + ' · ' + pt.score.toFixed(1).replace('.', ',') + '/10 · ' + pt.notes);

      var dot = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
      dot.setAttribute('cx', pt.x);
      dot.setAttribute('cy', pt.y);
      dot.setAttribute('r', 5);
      dot.setAttribute('class', 'cv-v3-reliability__point-visible');
      dot.setAttribute('data-sev', sev);

      pointsG.appendChild(hit);
      pointsG.appendChild(dot);
    });

    // X-axis bracket labels
    if (xaxisG) {
      while (xaxisG.firstChild) xaxisG.removeChild(xaxisG.firstChild);
      pts.forEach(function (pt) {
        var text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
        text.setAttribute('x', pt.x);
        text.setAttribute('y', yBottom + 22);
        text.setAttribute('text-anchor', 'middle');
        text.textContent = pt.bracket;
        xaxisG.appendChild(text);
      });
    }

    // Animate path draw on scroll-into-view
    function animatePath() {
      try {
        var len = pathEl.getTotalLength();
        pathEl.style.strokeDasharray = len + ' ' + len;
        pathEl.style.strokeDashoffset = len;
        // Force reflow before setting final state
        void pathEl.getBoundingClientRect();
        root.classList.add('is-animated');
        requestAnimationFrame(function () {
          pathEl.style.strokeDashoffset = '0';
        });
      } catch (e) {
        root.classList.add('is-animated');
      }
    }

    if (prefersReducedMotion) {
      root.classList.add('is-animated');
    } else {
      var io2 = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting && !root.classList.contains('is-animated')) {
            animatePath();
            io2.unobserve(root);
          }
        });
      }, { threshold: 0.3 });
      io2.observe(root);
    }

    // Tooltip on hover/focus
    if (tooltip) {
      var wrap = root.querySelector('.cv-v3-reliability__svg-wrap');
      function showTooltip(idx, anchorRect) {
        var pt = pts[idx];
        if (!pt) return;
        clearChildren(tooltip);
        var bracket = document.createElement('div');
        bracket.textContent = pt.bracket + ' km';
        tooltip.appendChild(bracket);
        var score = document.createElement('strong');
        score.textContent = pt.score.toFixed(1).replace('.', ',') + '/10';
        tooltip.appendChild(score);
        if (pt.notes) {
          var notes = document.createElement('div');
          notes.style.opacity = '0.8';
          notes.style.fontSize = '1.1rem';
          notes.textContent = pt.notes;
          tooltip.appendChild(notes);
        }
        // Position: anchor on the point's center in the SVG wrap
        var wrapRect = wrap.getBoundingClientRect();
        var svgRect = svg.getBoundingClientRect();
        var xPct = pt.x / 640;
        var yPct = pt.y / 280;
        var left = svgRect.left - wrapRect.left + xPct * svgRect.width;
        var top = svgRect.top - wrapRect.top + yPct * svgRect.height;
        tooltip.style.left = left + 'px';
        tooltip.style.top = top + 'px';
        tooltip.setAttribute('aria-hidden', 'false');
        tooltip.classList.add('is-visible');
      }
      function hideTooltip() {
        tooltip.setAttribute('aria-hidden', 'true');
        tooltip.classList.remove('is-visible');
      }
      pointsG.addEventListener('mouseover', function (e) {
        var hit = e.target.closest('.cv-v3-reliability__point-hit');
        if (hit) showTooltip(parseInt(hit.dataset.idx, 10), hit.getBoundingClientRect());
      });
      pointsG.addEventListener('focusin', function (e) {
        var hit = e.target.closest('.cv-v3-reliability__point-hit');
        if (hit) showTooltip(parseInt(hit.dataset.idx, 10), hit.getBoundingClientRect());
      });
      pointsG.addEventListener('mouseout', function (e) {
        if (e.relatedTarget && e.relatedTarget.closest && e.relatedTarget.closest('.cv-v3-reliability__point-hit')) return;
        hideTooltip();
      });
      pointsG.addEventListener('focusout', hideTooltip);
    }
  }

  // ---------- quiz -----------------------------------------------------------
  function initQuiz() {
    var quizzes = document.querySelectorAll('.cv-v3-quiz[data-cv-quiz]');
    if (!quizzes.length) return;
    quizzes.forEach(function (root) {
      var steps = Array.prototype.slice.call(root.querySelectorAll('.cv-v3-quiz__step'));
      if (!steps.length) return;
      var prevBtn = root.querySelector('[data-quiz-prev]');
      var nextBtn = root.querySelector('[data-quiz-next]');
      var submitBtn = root.querySelector('[data-quiz-submit]');
      var resultEl = root.querySelector('[data-quiz-result]');
      var dots = Array.prototype.slice.call(root.querySelectorAll('.cv-v3-quiz__progress-dot'));
      var rules = [];
      try { rules = JSON.parse(root.dataset.cvQuizRules || '[]'); }
      catch (e) { rules = []; }
      var current = 0;

      function show(index) {
        steps.forEach(function (s, i) { s.hidden = (i !== index); });
        dots.forEach(function (d, i) {
          d.classList.toggle('cv-v3-quiz__progress-dot--active', i === index);
          d.classList.toggle('cv-v3-quiz__progress-dot--done', i < index);
        });
        if (prevBtn) prevBtn.disabled = (index === 0);
        if (nextBtn) nextBtn.hidden = (index === steps.length - 1);
        if (submitBtn) submitBtn.hidden = (index !== steps.length - 1);
      }

      function animateTo(index, dir) {
        if (prefersReducedMotion || !steps[index].animate) { show(index); return; }
        show(index);
        var incoming = steps[index];
        incoming.animate(
          [
            { transform: 'translateX(' + (dir * 24) + 'px)', opacity: 0 },
            { transform: 'translateX(0)', opacity: 1 }
          ],
          { duration: 320, easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)', fill: 'both' }
        );
      }

      function goNext() {
        if (current < steps.length - 1) { current++; animateTo(current, 1); }
      }
      function goPrev() {
        if (current > 0) { current--; animateTo(current, -1); }
      }
      function restart() {
        current = 0;
        if (resultEl) resultEl.hidden = true;
        show(0);
      }
      function submit() {
        // Base score 6 = neutral-tilted-positive; rule modifiers push ±1–2.
        // With no rules (metafield empty), result = "GO AVEC RÉSERVES" by default,
        // which is the honest stance for an unseen usage pattern.
        var score = 6;
        var profileParts = [];
        steps.forEach(function (s, i) {
          var checked = s.querySelector('input[type=radio]:checked');
          if (!checked) return;
          var val = checked.value;
          profileParts.push(checked.dataset.label || val);
          var rule = rules.find(function (r) { return r.question_id === ('q' + (i + 1)) && r.answer === val; });
          if (rule && typeof rule.modifier === 'number') score += rule.modifier;
        });
        score = Math.max(0, Math.min(10, score));
        var verdict = score >= 7.5
          ? { label: 'GO', color: 'oklch(0.67 0.16 152)', icon: 'shield', mod: 'go' }
          : score >= 5.5
            ? { label: 'GO AVEC RÉSERVES', color: 'oklch(0.8 0.12 90)', icon: 'alert', mod: 'warn' }
            : { label: 'STOP', color: 'oklch(0.6 0.17 25)', icon: 'alert', mod: 'stop' };
        if (resultEl) {
          setChildText(resultEl, '[data-quiz-result-score]', score.toFixed(1).replace('.', ',') + '/10');
          var verdictEl = resultEl.querySelector('[data-quiz-result-verdict]');
          if (verdictEl) { verdictEl.textContent = verdict.label; verdictEl.style.color = verdict.color; }
          setChildText(resultEl, '[data-quiz-result-profile]', profileParts.join(' · '));
          // Icon slot
          var iconWrap = resultEl.querySelector('[data-quiz-result-icon]');
          if (iconWrap) {
            clearChildren(iconWrap);
            var svgNS2 = 'http://www.w3.org/2000/svg';
            var svgI = document.createElementNS(svgNS2, 'svg');
            svgI.setAttribute('class', 'cv-v3-quiz__result-icon cv-v3-quiz__result-icon--' + verdict.mod);
            svgI.setAttribute('viewBox', '0 0 24 24');
            svgI.setAttribute('aria-hidden', 'true');
            var use = document.createElementNS(svgNS2, 'use');
            use.setAttribute('href', '#cv-v3-icon-' + verdict.icon);
            use.setAttribute('xlink:href', '#cv-v3-icon-' + verdict.icon);
            svgI.appendChild(use);
            iconWrap.appendChild(svgI);
          }
          // Actions slot (restart + CTA)
          var actions = resultEl.querySelector('[data-quiz-result-actions]');
          if (actions && !actions.childNodes.length) {
            var restartBtn = document.createElement('button');
            restartBtn.type = 'button';
            restartBtn.className = 'cv-v3-quiz__btn cv-v3-quiz__btn--ghost';
            restartBtn.textContent = '↻ Refaire le quiz';
            restartBtn.addEventListener('click', function (e) { e.preventDefault(); restart(); });
            var ctaSource = document.querySelector('a.cv-v2-cta-primary, a.cv-article-v2__cta, a[href*="/products/rapport"]');
            var ctaHref = (ctaSource && ctaSource.getAttribute('href')) || '/products/rapport-historique-voiture';
            var ctaLink = document.createElement('a');
            ctaLink.className = 'cv-v3-quiz__btn cv-v3-quiz__btn--primary-cta';
            ctaLink.href = ctaHref;
            ctaLink.textContent = 'Vérifier cette voiture →';
            actions.appendChild(restartBtn);
            actions.appendChild(ctaLink);
          }
          resultEl.hidden = false;
          if (!prefersReducedMotion && resultEl.animate) {
            resultEl.animate(
              [ { opacity: 0, transform: 'translateY(12px)' }, { opacity: 1, transform: 'translateY(0)' } ],
              { duration: 360, easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)', fill: 'both' }
            );
          }
          resultEl.scrollIntoView({ behavior: prefersReducedMotion ? 'auto' : 'smooth', block: 'center' });
        }
      }

      if (prevBtn) prevBtn.addEventListener('click', function (e) { e.preventDefault(); goPrev(); });
      if (nextBtn) nextBtn.addEventListener('click', function (e) { e.preventDefault(); goNext(); });
      if (submitBtn) submitBtn.addEventListener('click', function (e) { e.preventDefault(); submit(); });
      show(0);
    });
  }

  // ---------- TCO calc -------------------------------------------------------
  function initTCO() {
    var roots = document.querySelectorAll('.cv-v3-tco[data-cv-tco]');
    if (!roots.length) return;
    roots.forEach(function (root) {
      var consoL100 = parseFloat(root.dataset.tcoConsoL100 || '0');
      if (!isFinite(consoL100) || consoL100 <= 0) {
        root.dataset.disabled = 'true';
        if (!root.querySelector('.cv-v3-tco__notice')) {
          var notice = document.createElement('p');
          notice.className = 'cv-v3-tco__notice';
          notice.textContent = 'Calcul disponible dès que les données de consommation sont validées par notre équipe.';
          if (root.firstChild) root.insertBefore(notice, root.firstChild.nextSibling);
          else root.appendChild(notice);
        }
        return;
      }
      var kmInput = root.querySelector('[data-tco-km]');
      var entInput = root.querySelector('[data-tco-entretien]');
      var gazPrix = parseFloat(root.dataset.tcoGazPrix || '1.8');
      var cotePerYear = parseFloat(root.dataset.tcoCoteYearly || '1500');
      var assurYear = parseFloat(root.dataset.tcoAssurYear || '750');
      var years = parseInt(root.dataset.tcoYears || '5', 10);

      var recompute = function () {
        var km = Math.max(0, parseFloat((kmInput && kmInput.value) || '15000'));
        var ent = Math.max(0, parseFloat((entInput && entInput.value) || '800'));
        var conso = (km * consoL100 / 100) * gazPrix;
        var cote = cotePerYear * years;
        var assur = assurYear * years;
        var entretien = ent * 12 * years;
        var total = (conso * years) + entretien + cote + assur;

        root.classList.add('is-recomputing');
        setTimeout(function () { root.classList.remove('is-recomputing'); }, 200);

        setChildText(root, '[data-tco-total]', fmtFR(total) + ' €');
        setChildText(root, '[data-tco-years]', String(years));
        setChildText(root, '[data-tco-conso]', fmtFR(conso * years) + ' €');
        setChildText(root, '[data-tco-entretien-out]', fmtFR(entretien) + ' €');
        setChildText(root, '[data-tco-cote]', fmtFR(cote) + ' €');
        setChildText(root, '[data-tco-assurance]', fmtFR(assur) + ' €');
      };

      var debounced = debounce(recompute, 200);
      if (kmInput) kmInput.addEventListener('input', debounced);
      if (entInput) entInput.addEventListener('input', debounced);
      recompute();
    });
  }

  // ---------- OBD tooltips ---------------------------------------------------
  var OBD_LIB = {
    'P0171': { name: 'Mélange trop pauvre (banc 1)', severity: 'Moyen', sev: 'medium', cost: '80–250 €' },
    'P0172': { name: 'Mélange trop riche (banc 1)', severity: 'Moyen', sev: 'medium', cost: '80–250 €' },
    'P0234': { name: 'Suralimentation turbo excessive', severity: 'Élevé', sev: 'high', cost: '600–1\u202f800 €' },
    'P0299': { name: 'Sous-alimentation turbo', severity: 'Élevé', sev: 'high', cost: '500–1\u202f500 €' },
    'P0300': { name: 'Mise-feu aléatoire détectée', severity: 'Moyen', sev: 'medium', cost: '120–500 €' },
    'P0301': { name: 'Mise-feu cylindre 1', severity: 'Moyen', sev: 'medium', cost: '100–400 €' },
    'P0302': { name: 'Mise-feu cylindre 2', severity: 'Moyen', sev: 'medium', cost: '100–400 €' },
    'P0303': { name: 'Mise-feu cylindre 3', severity: 'Moyen', sev: 'medium', cost: '100–400 €' },
    'P0304': { name: 'Mise-feu cylindre 4', severity: 'Moyen', sev: 'medium', cost: '100–400 €' },
    'P0401': { name: 'Débit EGR insuffisant', severity: 'Moyen', sev: 'medium', cost: '150–500 €' },
    'P0420': { name: 'Rendement catalyseur bas (banc 1)', severity: 'Élevé', sev: 'high', cost: '400–1\u202f500 €' },
    'P0524': { name: "Pression d'huile insuffisante", severity: 'Critique', sev: 'critical', cost: '800–3\u202f000 €' },
    'P2452': { name: 'Capteur pression FAP HS', severity: 'Moyen', sev: 'medium', cost: '180–450 €' },
    'P2463': { name: 'Accumulation suie FAP', severity: 'Élevé', sev: 'high', cost: '600–2\u202f500 €' },
    'P20EE': { name: 'Qualité AdBlue insuffisante', severity: 'Moyen', sev: 'medium', cost: '120–350 €' },
    'P0A0F': { name: 'Courant batterie haute tension anormal', severity: 'Critique', sev: 'critical', cost: '3\u202f000–12\u202f000 €' },
    'P0A80': { name: 'Remplacement pack batterie nécessaire', severity: 'Critique', sev: 'critical', cost: '4\u202f000–15\u202f000 €' },
    'P0AA6': { name: 'Isolation batterie haute tension', severity: 'Critique', sev: 'critical', cost: '1\u202f500–8\u202f000 €' },
    'P1A14': { name: 'Gestion batterie hybride', severity: 'Élevé', sev: 'high', cost: '800–2\u202f500 €' }
  };

  function initOBDTooltips() {
    var tooltip = document.querySelector('.cv-v3-tooltip');
    if (!tooltip) {
      tooltip = document.createElement('div');
      tooltip.className = 'cv-v3-tooltip';
      tooltip.setAttribute('role', 'tooltip');
      document.body.appendChild(tooltip);
    }
    var currentCode = null;
    var currentAnchor = null;

    function renderTooltip(code) {
      var info = OBD_LIB[code.toUpperCase()] || { name: 'Code diagnostic OBD-II', severity: 'À vérifier', sev: 'medium', cost: '—' };
      clearChildren(tooltip);
      tooltip.appendChild(makeDiv('cv-v3-tooltip__code', code));
      tooltip.appendChild(makeDiv('', info.name));
      tooltip.appendChild(makeDiv('cv-v3-tooltip__meta', info.severity + ' · ' + info.cost + ' · voir fiche →'));
      return info.sev || 'medium';
    }
    function positionTooltip(el) {
      var r = el.getBoundingClientRect();
      var tw = tooltip.offsetWidth;
      var left = r.left + r.width / 2 - tw / 2;
      left = Math.max(8, Math.min(window.innerWidth - tw - 8, left));
      var top = r.top - tooltip.offsetHeight - 8;
      if (top < 8) top = r.bottom + 8;
      tooltip.style.left = Math.round(left) + 'px';
      tooltip.style.top = Math.round(top) + 'px';
    }
    function handle(e) {
      var target = e.target.closest && e.target.closest('a[href*="/pages/code-defaut/"]');
      if (!target) return;
      var match = (target.getAttribute('href') || '').match(/p-([0-9a-z]+)/i);
      if (!match) return;
      var code = ('P' + match[1]).toUpperCase();
      if (code === currentCode && tooltip.classList.contains('is-visible')) return;
      if (currentAnchor) currentAnchor.removeAttribute('data-cv-obd-active');
      currentCode = code;
      var sev = renderTooltip(code);
      positionTooltip(target);
      target.setAttribute('data-cv-obd-active', sev);
      currentAnchor = target;
      tooltip.classList.add('is-visible');
    }
    function hide() {
      tooltip.classList.remove('is-visible');
      if (currentAnchor) { currentAnchor.removeAttribute('data-cv-obd-active'); currentAnchor = null; }
      currentCode = null;
    }
    document.addEventListener('mouseover', handle, { passive: true });
    document.addEventListener('focusin', handle, { passive: true });
    document.addEventListener('mouseout', function (e) {
      var rel = e.relatedTarget;
      if (rel && rel.closest && rel.closest('a[href*="/pages/code-defaut/"]')) return;
      hide();
    }, { passive: true });
    document.addEventListener('focusout', hide, { passive: true });
    window.addEventListener('scroll', hide, { passive: true });
  }

  onReady(function () {
    try { initProgress(); } catch (e) {}
    try { initVerdictPill(); } catch (e) {}
    try { initProblemSeverity(); } catch (e) {}
    try { initRadars(); } catch (e) {}
    try { initReliability(); } catch (e) {}
    try { initQuiz(); } catch (e) {}
    try { initTCO(); } catch (e) {}
    try { initOBDTooltips(); } catch (e) {}
    document.documentElement.classList.add('cv-v3-ready');
  });
})();
